import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useGifting } from '../../../../queries/Gifting/useGifting';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { styleSheet } from './styles';

interface IHandwrittenCardBalanceDisplayProps {
	account: Api.IAccount;
}

export const HandwrittenCardBalanceDisplay: React.FC<IHandwrittenCardBalanceDisplayProps> = ({ account }) => {
	const impersonationContext: Api.IImpersonationContext = { account: { id: account.id } };
	const { data: giftingBalance, isLoading, error } = useGifting({ impersonationContext });

	const getBalanceTitle = () => {
		return <div className={css(styleSheet.popoverTitle, styleSheet.balanceDisplayTitle)}>Handwritten Card Balance</div>;
	};

	if (isLoading) {
		return (
			<>
				{getBalanceTitle()}
				<div className={css(styleSheet.popoverKeyValue)}>
					<LoadingSpinner type='tiny' />
				</div>
			</>
		);
	}

	if (error) {
		return (
			<>
				{getBalanceTitle()}
				<div className={css(styleSheet.popoverKeyValue)}>
					<span className={css(styleSheet.providerNotConfigured)}>Error loading balance</span>
				</div>
			</>
		);
	}

	if (!giftingBalance) {
		return (
			<>
				{getBalanceTitle()}
				<div className={css(styleSheet.popoverKeyValue)}>
					<span className={css(styleSheet.providerNotConfigured)}>No balance data. Setup may not be complete</span>
				</div>
			</>
		);
	}

	const costPerCredit = account.features?.handwrittenCards?.currencyCostPerCredit;
	const hasCurrencyConversion = costPerCredit != null && costPerCredit > 0;

	const getCurrencyFromCredits = (credits: number) => {
		if (!hasCurrencyConversion) {
			return credits;
		}
		return credits * costPerCredit;
	};

	const getDisplayValue = (value: number) => {
		if (!hasCurrencyConversion) {
			return `${value} credits`;
		}
		return `$${value.toFixed(2)}`;
	};

	// The API returns the currency value for the available credits directly. But the others are returned as credits
	// that need to be converted to currency using the cost per credit.
	const availableBalance = giftingBalance.availableBalance ?? 0;
	const pendingBalance = getCurrencyFromCredits(giftingBalance.pendingCredits ?? 0);
	const totalCreditsAddedBalance = getCurrencyFromCredits(giftingBalance.totalCreditsAdded ?? 0);
	const appliedCoupon = account.planDetails?.cardCouponApplied;

	return (
		<>
			{getBalanceTitle()}
			<div className={css(styleSheet.popoverKeyValue)}>
				<span>Available Credits</span>
				<span>{getDisplayValue(availableBalance)}</span>
			</div>
			<div className={css(styleSheet.popoverKeyValue)}>
				<span>Pending Credits</span>
				<span>{getDisplayValue(pendingBalance)}</span>
			</div>
			<div className={css(styleSheet.popoverKeyValue)}>
				<span>Applied Coupon</span>
				<span>{appliedCoupon || '-'}</span>
			</div>
			<div className={css(styleSheet.popoverKeyValue)}>
				<span>Total Credits Added</span>
				<span>{getDisplayValue(totalCreditsAddedBalance)}</span>
			</div>
		</>
	);
};
